import {
  createBrowserRouter,
  createHashRouter,
  Navigate,
  type RouteObject,
} from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { AuthLayout } from '@/layouts/AuthLayout';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { GuestRoute, ProtectedRoute, PermissionRoute, RoleRoute, OfficeBearerRoute, MarketingRoute } from '@/routes/guards';
import { PERMISSIONS } from '@/permissions/permissionRegistry';
import { ROLES } from '@/constants';
import { AppLoader } from '@/components/common/AppLoader';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { DeepLinkBridge } from '@/mobile/components/DeepLinkBridge';
import { isMarketingUser } from '@/utils/helpers';
import { MarketingLayout } from '@/modules/marketing/layouts/MarketingLayout';
import {
  LoginPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  ChangePasswordPage,
  ProfilePage,
  ForbiddenPage,
  DashboardPage,
  SocietyListPage,
  SocietyRegisterPage,
  SocietyDetailPage,
  SocietySettingsPage,
  ComplaintDashboardPage,
  ComplaintListPage,
  ComplaintCreatePage,
  ComplaintDetailPage,
  ServiceDirectoryPage,
  ServiceRequestListPage,
  ServiceRequestCreatePage,
  ServiceRequestDetailPage,
  ProviderActionPage,
  VisitorDeskPage,
  VisitorDetailPage,
  SecurityCamerasPage,
  NoticeListPage,
  NoticeCreatePage,
  NoticeDetailPage,
  WhatsAppLogsPage,
  ResidentListPage,
  ResidentCreatePage,
  ResidentDetailPage,
  HouseListPage,
  HouseCreatePage,
  HouseDetailPage,
  HouseEditPage,
  MeetingListPage,
  MeetingCreatePage,
  MeetingDetailPage,
  BankingPage,
  FinancialReportsPage,
  PaymentReconciliationPage,
  CorpusFundPage,
  CollectionListPage,
  CollectionCreatePage,
  CollectionDetailPage,
  OwnerPaymentsPage,
  SocietyFundListPage,
  SocietyFundInitiatePage,
  SocietyFundDetailPage,
  ExpenseListPage,
  ExpenseCreatePage,
  ExpenseDetailPage,
  ExpenseGroupListPage,
  ExpenseGroupCreatePage,
  ExpenseGroupDetailPage,
  WaterTankerListPage,
  WaterTankerCreatePage,
  WaterTankerDetailPage,
  SocietyLetterheadPage,
  ReimbursementListPage,
  ReimbursementClaimPage,
  EventDashboardPage,
  EventListPage,
  EventCreatePage,
  EventDetailPage,
  EventCalendarPage,
  ElectionListPage,
  ElectionCreatePage,
  ElectionDetailPage,
  ElectionDashboardPage,
  CandidateNominatePage,
  VotingPage,
  ElectionResultsPage,
  CertificateVerificationPage,
  ComingSoonPage,
  NotificationsPage,
  RolesPage,
  RolePermissionsPage,
  RbacAuditPage,
  GoogleCalendarSettingsPage,
  PetitionBoardPage,
  ResignationPage,
  LicensePage,
  LicenseInvoicesPage,
  SocietyStaffListPage,
  SocietyStaffCreatePage,
  SocietyStaffDetailPage,
  SocietyIntegrationsPage,
  MenuCustomizationPage,
  ImportHubPage,
  ImportHousesPage,
  ImportResidentsPage,
  ImportExpensesPage,
  WatchmanLayout,
  WatchmanDashboardPage,
  WatchmanScanPage,
  WatchmanActiveVisitorsPage,
  WatchmanEntriesListPage,
  WatchmanIncidentPage,
  WatchmanShiftPage,
  GatePassListPage,
  GatePassCreatePage,
  SecurityAdminDashboardPage,
  SecurityNotificationSettingsPage,
  SecurityGatesPage,
  SecurityShiftsReportPage,
  EmergencyListPage,
  EmergencyDetailPage,
  SuperAdminDashboardPage,
  SuperAdminSocietiesPage,
  SuperAdminSocietyCreatePage,
  SuperAdminSocietyDetailPage,
  SuperAdminPlansPage,
  SuperAdminLicensesPage,
  SuperAdminSubscriptionsPage,
  SuperAdminTrialsPage,
  SuperAdminInvoicesPage,
  SuperAdminUsersPage,
  SuperAdminFeaturesPage,
  SuperAdminAnalyticsPage,
  SuperAdminAuditLogsPage,
  SuperAdminSystemHealthPage,
  SuperAdminSettingsPage,
  SuperAdminSocietyTypesPage,
  SuperAdminMaintenancePage,
  SuperAdminPricingOverridesPage,
  SuperAdminCouponsPage,
  SuperAdminReferralsPage,
  SuperAdminMarketingPage,
  SuperAdminQACenterPage,
  PromotionDashboardPage,
  PromotionProspectsPage,
  PromotionCampaignsPage,
  PromotionCampaignCreatePage,
  PromotionCampaignDetailPage,
  PromotionEmailPage,
  PromotionWhatsAppPage,
  PromotionSmsPage,
  PromotionTemplatesPage,
  PromotionSenderProfilesPage,
  PromotionOtherChannelsPage,
  PromotionAnalyticsPage,
  PromotionSettingsPage,
  MarketingDashboardPage,
  MarketingReferralsPage,
  MarketingCommissionsPage,
  MarketingPayoutsPage,
  MarketingProfilePage,
  BlockListPage,
  InvitationListPage,
  InvitationCreatePage,
  InvitationDetailPage,
  ForumFeedPage,
  ForumCreatePostPage,
  ForumPostDetailPage,
  AiChatPage,
  AiConfigPage,
  DocumentationListPage,
  DocumentationDetailPage,
  RoleMatrixPage,
  CoverageAuditPage,
  SocietyTicketListPage,
  SocietyTicketDetailPage,
  SuperAdminTicketListPage,
  SuperAdminTicketDetailPage,
  SuperAdminFeatureRequestsPage,
  SuperAdminDataCleanupPage,
  AmenityListPage,
  AmenityDetailPage,
  MyBookingsPage,
  AmenityAdminDashboardPage,
  FinancialStatementsPage,
  PollsListPage,
  PollAdminDashboardPage,
  DocumentVaultPage,
  DocumentDetailPage,
  SuperAdminWebsitePage,
} from '@/routes/lazyPages';
import { useAuthStore } from '@/store/authStore';
import { useSuperAdminAuthStore } from '@/store/superAdminAuthStore';
import { Outlet } from 'react-router-dom';

function SuperAdminGuard() {
  const hydrated = useAuthStore((s) => s.hydrated);
  const authState = useAuthStore((s) => s.authState);
  const isBiometricLocked = useAuthStore((s) => s.isBiometricLocked);
  const isPlatformAuth = useSuperAdminAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);

  if (!hydrated || authState === 'AUTH_BOOTSTRAPPING') {
    return <AppLoader label="Loading..." />;
  }

  if (isBiometricLocked || authState === 'BIOMETRIC_REQUIRED') {
    return <Navigate to="/login" replace state={{ biometricRequired: true }} />;
  }

  if (isMarketingUser(user)) {
    return <Navigate to="/marketing" replace />;
  }

  const isMainAuthSuperAdmin = Boolean(
    user?.roles?.includes('SUPER_ADMIN') ||
    (user as any)?.role === 'SUPER_ADMIN' ||
    ((user as any)?.scope === 'PLATFORM' && !isMarketingUser(user)),
  );

  if (!isPlatformAuth && !isMainAuthSuperAdmin) {
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
}

const routes: RouteObject[] = [
  {
    element: <DeepLinkBridge />,
    errorElement: <ErrorBoundary />,
    children: [
      { path: '/provider-action/:token', element: <ProviderActionPage /> },
      { path: '/verify/election-certificate/:certificateNumber', element: <CertificateVerificationPage /> },
      {
        element: <GuestRoute />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              { path: '/login', element: <LoginPage /> },
              { path: '/forgot-password', element: <ForgotPasswordPage /> },
              { path: '/reset-password', element: <ResetPasswordPage /> },
            ],
          },
        ],
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              { path: '/', element: <Navigate to="/dashboard" replace /> },
              { path: '/dashboard', element: <DashboardPage /> },
              { path: '/m/home', element: <Navigate to="/dashboard" replace /> },
              { path: '/profile', element: <ProfilePage /> },
              { path: '/change-password', element: <ChangePasswordPage /> },
              { path: '/403', element: <ForbiddenPage /> },
              { path: '/settings/license', element: <LicensePage /> },
              {
                element: <RoleRoute roles={[ROLES.PRESIDENT, ROLES.TREASURER]} />,
                children: [{ path: '/settings/license/invoices', element: <LicenseInvoicesPage /> }],
              },
              {
                element: <PermissionRoute featureKey="society.my_society" permission={PERMISSIONS.society.read} />,
                children: [{ path: '/society', element: <SocietySettingsPage /> }],
              },
              {
                element: <PermissionRoute featureKey="operations.complaints" permission={PERMISSIONS.complaint.read} />,
                children: [
                  { path: '/complaints/dashboard', element: <ComplaintDashboardPage /> },
                  { path: '/complaints', element: <ComplaintListPage /> },
                  { path: '/complaints/new', element: <ComplaintCreatePage /> },
                  { path: '/complaints/:id', element: <ComplaintDetailPage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="operations.services" permission={PERMISSIONS.service.read} />,
                children: [
                  { path: '/services', element: <ServiceDirectoryPage /> },
                  { path: '/services/requests', element: <ServiceRequestListPage /> },
                  { path: '/services/requests/new', element: <ServiceRequestCreatePage /> },
                  { path: '/services/requests/:id', element: <ServiceRequestDetailPage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="communication.notices" permission={PERMISSIONS.notice.read} />,
                children: [
                  { path: '/notices', element: <NoticeListPage /> },
                  { path: '/notices/new', element: <NoticeCreatePage /> },
                  { path: '/notices/:id', element: <NoticeDetailPage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="communication.whatsapp" permission={PERMISSIONS.whatsapp.read} />,
                children: [{ path: '/whatsapp/logs', element: <WhatsAppLogsPage /> }],
              },
              {
                element: <PermissionRoute featureKey="communication.events" permission={PERMISSIONS.event.read} />,
                children: [
                  { path: '/events/dashboard', element: <EventDashboardPage /> },
                  { path: '/events/calendar', element: <EventCalendarPage /> },
                  { path: '/events/new', element: <EventCreatePage /> },
                  { path: '/events/:id', element: <EventDetailPage /> },
                  { path: '/events', element: <EventListPage /> },
                ],
              },
              {
                element: (
                  <PermissionRoute featureKey="society" anyOf={[PERMISSIONS.resident.read, PERMISSIONS.house.read]} />
                ),
                children: [
                  { path: '/residents/new', element: <ResidentCreatePage /> },
                  { path: '/residents/:id', element: <ResidentDetailPage /> },
                  { path: '/residents', element: <ResidentListPage /> },
                  { path: '/houses/new', element: <HouseCreatePage /> },
                  { path: '/houses/:id/edit', element: <HouseEditPage /> },
                  { path: '/houses/:id', element: <HouseDetailPage /> },
                  { path: '/houses', element: <HouseListPage /> },
                  { path: '/properties/new', element: <HouseCreatePage /> },
                  { path: '/properties/:id/edit', element: <HouseEditPage /> },
                  { path: '/properties/:id', element: <HouseDetailPage /> },
                  { path: '/properties', element: <HouseListPage /> },
                  { path: '/blocks', element: <BlockListPage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="communication.invitations" anyOf={[PERMISSIONS.event.read, PERMISSIONS.notice.read]} />,
                children: [
                  { path: '/invitations', element: <InvitationListPage /> },
                  { path: '/invitations/new', element: <InvitationCreatePage /> },
                  { path: '/invitations/:id', element: <InvitationDetailPage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="communication.forum" anyOf={[PERMISSIONS.notice.read, PERMISSIONS.complaint.read]} />,
                children: [
                  { path: '/forum', element: <ForumFeedPage /> },
                  { path: '/forum/new', element: <ForumCreatePostPage /> },
                  { path: '/forum/:id', element: <ForumPostDetailPage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="society.staff" permission={PERMISSIONS.staff.read} />,
                children: [
                  { path: '/staff/new', element: <SocietyStaffCreatePage /> },
                  { path: '/staff/:id', element: <SocietyStaffDetailPage /> },
                  { path: '/staff', element: <SocietyStaffListPage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="operations.meetings" permission={PERMISSIONS.meeting.read} />,
                children: [
                  { path: '/meetings/new', element: <MeetingCreatePage /> },
                  { path: '/meetings/:id', element: <MeetingDetailPage /> },
                  { path: '/meetings', element: <MeetingListPage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="finance" permission={PERMISSIONS.finance.read} />,
                children: [
                  { path: '/banking', element: <BankingPage /> },
                  { path: '/finance/reports', element: <FinancialReportsPage /> },
                  { path: '/reports/financial', element: <FinancialReportsPage /> },
                  { path: '/finance/statements', element: <FinancialStatementsPage /> },
                  { path: '/statements', element: <FinancialStatementsPage /> },
                  { path: '/finance/reconciliation', element: <PaymentReconciliationPage /> },
                  { path: '/banking/reconciliation', element: <PaymentReconciliationPage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="finance.corpus_fund" anyOf={[PERMISSIONS.corpus.read, PERMISSIONS.finance.read]} />,
                children: [
                  { path: '/finance/corpus-fund', element: <CorpusFundPage /> },
                  { path: '/corpus-fund', element: <CorpusFundPage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="finance.maintenance" permission={PERMISSIONS.collection.read} />,
                children: [{ path: '/dues', element: <OwnerPaymentsPage /> }],
              },
              { path: '/notifications', element: <NotificationsPage /> },
              {
                element: <PermissionRoute featureKey="operations.amenities" />,
                children: [
                  { path: '/amenities', element: <AmenityListPage /> },
                  { path: '/amenities/my-bookings', element: <MyBookingsPage /> },
                  { path: '/amenities/admin', element: <AmenityAdminDashboardPage /> },
                  { path: '/amenities/:id', element: <AmenityDetailPage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="elections.polls" />,
                children: [
                  { path: '/polls', element: <PollsListPage /> },
                  { path: '/polls/admin', element: <PollAdminDashboardPage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="operations.documents" permission={PERMISSIONS.document.read} />,
                children: [
                  { path: '/documents', element: <DocumentVaultPage /> },
                  { path: '/documents/:id', element: <DocumentDetailPage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="security.visitors" permission={PERMISSIONS.visitor.read} />,
                children: [
                  { path: '/visitors', element: <VisitorDeskPage /> },
                  { path: '/visitors/:id', element: <VisitorDetailPage /> },
                ],
              },
              {
                element: (
                  <PermissionRoute
                    featureKey="security.gate_pass"
                    anyOf={[
                      PERMISSIONS.gatepass.view,
                      PERMISSIONS.gatepass.create,
                      PERMISSIONS.visitor.read,
                    ]}
                  />
                ),
                children: [
                  { path: '/gate-passes', element: <GatePassListPage /> },
                  { path: '/gate-passes/new', element: <GatePassCreatePage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="security.cc_cameras" />,
                children: [
                  { path: '/security/cameras', element: <SecurityCamerasPage /> },
                  { path: '/cameras', element: <SecurityCamerasPage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="security" anyOf={[PERMISSIONS.visitor.read, PERMISSIONS.society.read]} />,
                children: [
                  { path: '/security/admin', element: <SecurityAdminDashboardPage /> },
                  { path: '/security/gates', element: <SecurityGatesPage /> },
                  { path: '/security/shifts', element: <SecurityShiftsReportPage /> },
                  { path: '/security/notification-settings', element: <SecurityNotificationSettingsPage /> },
                ],
              },
              { path: '/gallery', element: <ComingSoonPage /> },
              { path: '/emergencies', element: <EmergencyListPage /> },
              { path: '/emergencies/:id', element: <EmergencyDetailPage /> },
              { path: '/emergency', element: <EmergencyListPage /> },
              { path: '/emergency/:id', element: <EmergencyDetailPage /> },
              {
                element: <PermissionRoute featureKey="elections.petitions" />,
                children: [{ path: '/governance/petitions', element: <PetitionBoardPage /> }],
              },
              {
                element: <OfficeBearerRoute />,
                children: [{ path: '/governance/resignation', element: <ResignationPage /> }],
              },
              {
                element: <PermissionRoute featureKey="finance.expenses" permission={PERMISSIONS.expense.read} />,
                children: [
                  { path: '/expenses/new', element: <ExpenseCreatePage /> },
                  { path: '/expenses', element: <ExpenseListPage /> },
                  { path: '/expenses/groups/new', element: <ExpenseGroupCreatePage /> },
                  { path: '/expenses/groups/:id', element: <ExpenseGroupDetailPage /> },
                  { path: '/expenses/groups', element: <ExpenseGroupListPage /> },
                  { path: '/water-tanker/new', element: <WaterTankerCreatePage /> },
                  { path: '/water-tanker/:id', element: <WaterTankerDetailPage /> },
                  { path: '/water-tanker', element: <WaterTankerListPage /> },
                ],
              },
              {
                element: (
                  <PermissionRoute featureKey="finance.reimbursements" anyOf={[PERMISSIONS.expense.read, PERMISSIONS.reimbursement.read]} />
                ),
                children: [
                  { path: '/expenses/:id', element: <ExpenseDetailPage /> },
                  { path: '/reimbursements/new', element: <ReimbursementClaimPage /> },
                  { path: '/reimbursements/:id', element: <ExpenseDetailPage /> },
                  { path: '/reimbursements', element: <ReimbursementListPage /> },
                ],
              },
              {
                element: (
                  <PermissionRoute
                    featureKey="society.import"
                    anyOf={[
                      PERMISSIONS.house.create,
                      PERMISSIONS.resident.create,
                      PERMISSIONS.expense.create,
                    ]}
                  />
                ),
                children: [
                  { path: '/import', element: <ImportHubPage /> },
                  { path: '/import/houses', element: <ImportHousesPage /> },
                  { path: '/import/residents', element: <ImportResidentsPage /> },
                  { path: '/import/expenses', element: <ImportExpensesPage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="elections.voting" permission={PERMISSIONS.election.read} />,
                children: [
                  { path: '/elections/dashboard', element: <ElectionDashboardPage /> },
                  { path: '/elections/new', element: <ElectionCreatePage /> },
                  { path: '/elections/:id/nominate', element: <CandidateNominatePage /> },
                  { path: '/elections/:id/vote', element: <VotingPage /> },
                  { path: '/elections/:id/results', element: <ElectionResultsPage /> },
                  { path: '/elections/:id', element: <ElectionDetailPage /> },
                  { path: '/elections', element: <ElectionListPage /> },
                ],
              },
              {
                element: <PermissionRoute featureKey="finance.collections" permission={PERMISSIONS.collection.read} />,
                children: [
                  { path: '/collections/society-fund/new', element: <SocietyFundInitiatePage /> },
                  { path: '/collections/society-fund/:id', element: <SocietyFundDetailPage /> },
                  { path: '/collections/society-fund', element: <SocietyFundListPage /> },
                  {
                    path: '/collections/special-collections/new',
                    element: <SocietyFundInitiatePage kind="special_levy" />,
                  },
                  {
                    path: '/collections/special-collections/:id',
                    element: <SocietyFundDetailPage kind="special_levy" />,
                  },
                  {
                    path: '/collections/special-collections',
                    element: <SocietyFundListPage kind="special_levy" />,
                  },
                  { path: '/collections/new', element: <CollectionCreatePage /> },
                  { path: '/collections/:id', element: <CollectionDetailPage /> },
                  { path: '/collections', element: <CollectionListPage /> },
                ],
              },
              {
                element: (
                  <PermissionRoute audience="platform" permission={PERMISSIONS.society.read} />
                ),
                children: [
                  { path: '/societies', element: <SocietyListPage /> },
                  { path: '/societies/register', element: <SocietyRegisterPage /> },
                  { path: '/societies/:id', element: <SocietyDetailPage /> },
                ],
              },
              {
                element: (
                  <RoleRoute roles={[ROLES.PRESIDENT, ROLES.VICE_PRESIDENT, ROLES.SUPER_ADMIN]} />
                ),
                children: [
                  { path: '/settings/roles', element: <RolesPage /> },
                  { path: '/settings/roles/:id/permissions', element: <RolePermissionsPage /> },
                ],
              },
              {
                element: (
                  <PermissionRoute anyOf={[PERMISSIONS.role.read, PERMISSIONS.permission.read]} />
                ),
                children: [
                  { path: '/settings/audit', element: <RbacAuditPage /> },
                ],
              },
              {
                element: <RoleRoute roles={[ROLES.SUPER_ADMIN]} />,
                children: [
                  { path: '/settings/google-calendar', element: <GoogleCalendarSettingsPage /> },
                  { path: '/testing/dashboard', element: <Navigate to="/super-admin/qa" replace /> },
                  { path: '/testing/explorer', element: <Navigate to="/super-admin/qa/suites" replace /> },
                  { path: '/testing/cases', element: <Navigate to="/super-admin/qa/cases" replace /> },
                  { path: '/testing/executions', element: <Navigate to="/super-admin/qa/runs" replace /> },
                  { path: '/testing/execute/:testCaseId', element: <Navigate to="/super-admin/qa/cases" replace /> },
                  { path: '/testing/coverage-matrix', element: <Navigate to="/super-admin/qa/coverage" replace /> },
                ],
              },
              {
                element: (
                  <PermissionRoute
                    anyOf={[
                      PERMISSIONS.society.update,
                      PERMISSIONS.whatsapp.read,
                      PERMISSIONS.whatsapp.update,
                      PERMISSIONS.society.read,
                    ]}
                  />
                ),
                children: [
                  { path: '/settings/integrations', element: <SocietyIntegrationsPage /> },
                  { path: '/society/integrations', element: <SocietyIntegrationsPage /> },
                  { path: '/settings/letterhead', element: <SocietyLetterheadPage /> },
                  { path: '/society/letterhead', element: <SocietyLetterheadPage /> },
                  { path: '/settings/navigation', element: <MenuCustomizationPage /> },
                  { path: '/society/navigation', element: <MenuCustomizationPage /> },
                ],
              },
              {
                element: (
                  <RoleRoute
                    roles={[
                      ROLES.PRESIDENT,
                      ROLES.VICE_PRESIDENT,
                      ROLES.GENERAL_SECRETARY,
                      ROLES.JOINT_SECRETARY,
                      ROLES.TREASURER,
                      ROLES.EXECUTIVE_MEMBER,
                      ROLES.COMMITTEE_MEMBER,
                    ]}
                  />
                ),
                children: [{ path: '/ai', element: <AiChatPage /> }],
              },
              {
                element: <RoleRoute roles={[ROLES.PRESIDENT, ROLES.VICE_PRESIDENT]} />,
                children: [{ path: '/settings/ai-config', element: <AiConfigPage /> }],
              },
              // Documentation Routes
              { path: '/documentation', element: <DocumentationListPage /> },
              { path: '/documentation/role-matrix', element: <RoleMatrixPage /> },
              { path: '/documentation/:docId', element: <DocumentationDetailPage /> },
              // Help & Support Routes
              { path: '/support', element: <SocietyTicketListPage /> },
              { path: '/support/tickets', element: <SocietyTicketListPage /> },
              { path: '/support/tickets/:ticketId', element: <SocietyTicketDetailPage /> },
            ],
          },
          {
            element: <RoleRoute roles={['WATCHMAN']} />,
            children: [
              {
                element: <WatchmanLayout />,
                children: [
                  { path: '/watchman/scan', element: <WatchmanScanPage /> },
                  { path: '/gate-pass/scan', element: <WatchmanScanPage /> },
                ],
              },
            ],
          },
          {
            element: <RoleRoute roles={['WATCHMAN', ROLES.PRESIDENT, ROLES.VICE_PRESIDENT]} />,
            children: [
              {
                element: <WatchmanLayout />,
                children: [
                  { path: '/watchman', element: <WatchmanDashboardPage /> },
                  { path: '/watchman/inside', element: <WatchmanActiveVisitorsPage /> },
                  { path: '/watchman/entries', element: <WatchmanEntriesListPage /> },
                  { path: '/watchman/incidents', element: <WatchmanIncidentPage /> },
                  { path: '/watchman/shift', element: <WatchmanShiftPage /> },
                ],
              },
            ],
          },
        ],
      },
      // Super Admin Platform Application Shell & Routes
      { path: '/super-admin/login', element: <Navigate to="/login" replace /> },
      {
        path: '/super-admin',
        element: <SuperAdminGuard />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              { index: true, element: <SuperAdminDashboardPage /> },
              { path: 'website', element: <SuperAdminWebsitePage /> },
              { path: 'promotion', element: <PromotionDashboardPage /> },
              { path: 'promotion/prospects', element: <PromotionProspectsPage /> },
              { path: 'promotion/campaigns', element: <PromotionCampaignsPage /> },
              { path: 'promotion/campaigns/new', element: <PromotionCampaignCreatePage /> },
              { path: 'promotion/campaigns/:id', element: <PromotionCampaignDetailPage /> },
              { path: 'promotion/templates', element: <PromotionTemplatesPage /> },
              { path: 'promotion/sender-profiles', element: <PromotionSenderProfilesPage /> },
              { path: 'promotion/email', element: <PromotionEmailPage /> },
              { path: 'promotion/whatsapp', element: <PromotionWhatsAppPage /> },
              { path: 'promotion/sms', element: <PromotionSmsPage /> },
              { path: 'promotion/other-channels', element: <PromotionOtherChannelsPage /> },
              { path: 'promotion/analytics', element: <PromotionAnalyticsPage /> },
              { path: 'promotion/settings', element: <PromotionSettingsPage /> },
              { path: 'societies', element: <SuperAdminSocietiesPage /> },
              { path: 'qa', element: <SuperAdminQACenterPage /> },
              { path: 'qa/*', element: <SuperAdminQACenterPage /> },
              { path: 'societies/new', element: <SuperAdminSocietyCreatePage /> },
              { path: 'societies/create', element: <SuperAdminSocietyCreatePage /> },
              { path: 'societies/:id', element: <SuperAdminSocietyDetailPage /> },

              { path: 'plans', element: <SuperAdminPlansPage /> },
              { path: 'feature-requests', element: <SuperAdminFeatureRequestsPage /> },
              { path: 'pricing-overrides', element: <SuperAdminPricingOverridesPage /> },
              { path: 'coupons', element: <SuperAdminCouponsPage /> },
              { path: 'referrals', element: <SuperAdminReferralsPage /> },
              { path: 'marketing', element: <SuperAdminMarketingPage /> },
              { path: 'licenses', element: <SuperAdminLicensesPage /> },
              { path: 'subscriptions', element: <SuperAdminSubscriptionsPage /> },
              { path: 'trials', element: <SuperAdminTrialsPage /> },
              { path: 'invoices', element: <SuperAdminInvoicesPage /> },
              { path: 'users', element: <SuperAdminUsersPage /> },
              { path: 'features', element: <SuperAdminFeaturesPage /> },
              { path: 'analytics', element: <SuperAdminAnalyticsPage /> },
              { path: 'audit-logs', element: <SuperAdminAuditLogsPage /> },
              { path: 'system-health', element: <SuperAdminSystemHealthPage /> },
              { path: 'society-types', element: <SuperAdminSocietyTypesPage /> },
              { path: 'tickets', element: <SuperAdminTicketListPage /> },
              { path: 'tickets/:ticketId', element: <SuperAdminTicketDetailPage /> },
              { path: 'documentation', element: <DocumentationListPage /> },
              { path: 'documentation/role-matrix', element: <RoleMatrixPage /> },
              { path: 'documentation/coverage', element: <CoverageAuditPage /> },
              { path: 'documentation/:docId', element: <DocumentationDetailPage /> },
              { path: 'testing/dashboard', element: <Navigate to="/super-admin/qa" replace /> },
              { path: 'testing/explorer', element: <Navigate to="/super-admin/qa/suites" replace /> },
              { path: 'testing/cases', element: <Navigate to="/super-admin/qa/cases" replace /> },
              { path: 'testing/executions', element: <Navigate to="/super-admin/qa/runs" replace /> },
              { path: 'testing/execute/:testCaseId', element: <Navigate to="/super-admin/qa/cases" replace /> },
              { path: 'testing/coverage-matrix', element: <Navigate to="/super-admin/qa/coverage" replace /> },
              { path: 'settings', element: <SuperAdminSettingsPage /> },
              { path: 'settings/google-calendar', element: <GoogleCalendarSettingsPage /> },
              { path: 'google-calendar', element: <GoogleCalendarSettingsPage /> },
              { path: 'data-cleanup', element: <SuperAdminDataCleanupPage /> },
              { path: 'maintenance', element: <SuperAdminMaintenancePage /> },
              { path: 'notifications', element: <NotificationsPage /> },
              { path: 'profile', element: <ProfilePage /> },
              { path: 'change-password', element: <ChangePasswordPage /> },
            ],
          },
        ],
      },
      // Marketing Person Platform Application Shell & Routes
      {
        path: '/marketing',
        element: <MarketingRoute />,
        children: [
          {
            element: <MarketingLayout />,
            children: [
              { index: true, element: <MarketingDashboardPage /> },
              { path: 'referrals', element: <MarketingReferralsPage /> },
              { path: 'commissions', element: <MarketingCommissionsPage /> },
              { path: 'payouts', element: <MarketingPayoutsPage /> },
              { path: 'profile', element: <MarketingProfilePage /> },
            ],
          },
        ],
      },
      { path: '*', element: <Navigate to="/dashboard" replace /> },
    ],
  },
];

/** Hash router on native for reliable deep asset paths; browser router on web. */
export function createAppRouter() {
  if (Capacitor.isNativePlatform()) {
    return createHashRouter(routes);
  }
  return createBrowserRouter(routes);
}

export const router = createAppRouter();
